// PATH: src/components/modules/devtools/panels/DbExplorerPanel.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { downloadWithAuth } from "../utils/devtoolsAuth";

const MAX_PREVIEW_ROWS = 200;

export default function DbExplorerPanel() {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState("");

  const [selectedProjectId, setSelectedProjectId] = useState("");

  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState("");
  const [tables, setTables] = useState([]);
  const [views, setViews] = useState([]);

  const [selectedTable, setSelectedTable] = useState("");

  const [resultLoading, setResultLoading] = useState(false);
  const [resultError, setResultError] = useState("");
  const [describeResult, setDescribeResult] = useState(null);
  const [selectResult, setSelectResult] = useState(null);

  const [diagramOpen, setDiagramOpen] = useState(false);
  const [diagramLoading, setDiagramLoading] = useState(false);
  const [diagramError, setDiagramError] = useState("");
  const [diagramMermaid, setDiagramMermaid] = useState("");
  const [diagramZoom, setDiagramZoom] = useState(1);
  const [diagramPan, setDiagramPan] = useState({ x: 0, y: 0 });
  const [diagramIsPanning, setDiagramIsPanning] = useState(false);
  const [diagramPanStart, setDiagramPanStart] = useState({ x: 0, y: 0 });
  const [showMermaidText, setShowMermaidText] = useState(false);

  const canAct = Boolean(selectedProjectId && selectedTable);

  useEffect(() => {
    const loadProjects = async () => {
      setProjectsLoading(true);
      setProjectsError("");
      try {
        const res = await fetch("/api/dev-ide/projects");
        const data = await res.json().catch(() => null);
        if (res.ok && data && data.success) {
          const list = data.projects || [];
          setProjects(list);
          if (!selectedProjectId && list.length > 0) {
            setSelectedProjectId(list[0].project_id);
          }
        } else {
          setProjectsError((data && data.error) || "Failed to load projects.");
        }
      } catch (e) {
        setProjectsError(e.message || String(e));
      } finally {
        setProjectsLoading(false);
      }
    };
    loadProjects();
  }, []);

  const loadTables = async (projectId) => {
    setTablesLoading(true);
    setTablesError("");
    setTables([]);
    setViews([]);
    setSelectedTable("");
    setDescribeResult(null);
    setSelectResult(null);

    try {
      const qs = new URLSearchParams({ project_id: projectId });
      const res = await fetch(`/api/dev-ide/db/tables?${qs.toString()}`);
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.success) {
        setTables(data.tables || []);
        setViews(data.views || []);
      } else {
        setTablesError((data && data.error) || "Failed to load tables.");
      }
    } catch (e) {
      setTablesError(e.message || String(e));
    } finally {
      setTablesLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProjectId) {
      loadTables(selectedProjectId);
    }
  }, [selectedProjectId]);

  const handleDescribe = async () => {
    if (!canAct) return;
    setResultLoading(true);
    setResultError("");
    setDescribeResult(null);
    setSelectResult(null);
    try {
      const qs = new URLSearchParams({
        project_id: selectedProjectId,
        table: selectedTable,
      });
      const res = await fetch(`/api/dev-ide/db/describe?${qs.toString()}`);
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.success) {
        setDescribeResult(data);
      } else {
        setResultError((data && data.error) || "Describe failed.");
      }
    } catch (e) {
      setResultError(e.message || String(e));
    } finally {
      setResultLoading(false);
    }
  };

  const handleSelectTop = async (limit = 50) => {
    if (!canAct) return;
    setResultLoading(true);
    setResultError("");
    setDescribeResult(null);
    setSelectResult(null);
    try {
      const qs = new URLSearchParams({
        project_id: selectedProjectId,
        table: selectedTable,
        limit: String(Math.min(Math.max(limit, 1), MAX_PREVIEW_ROWS)),
      });
      const res = await fetch(`/api/dev-ide/db/select?${qs.toString()}`);
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.success) {
        setSelectResult(data);
      } else {
        setResultError((data && data.error) || "Select failed.");
      }
    } catch (e) {
      setResultError(e.message || String(e));
    } finally {
      setResultLoading(false);
    }
  };

  const handleDownloadSchema = () => {
    if (!selectedProjectId) return;
    const qs = new URLSearchParams({ project_id: selectedProjectId });
    downloadWithAuth(`/api/dev-ide/db/schema/download?${qs.toString()}`).catch((err) => {
      setError(err.message || "Failed to download schema.");
    });
  };

  const encodeMermaidForInk = (mermaidText) => {
    try {
      // UTF-8 safe base64
      const utf8 = encodeURIComponent(mermaidText).replace(/%([0-9A-F]{2})/g, (_, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      );
      return btoa(utf8);
    } catch (e) {
      return "";
    }
  };

  const diagramSvgUrl = useMemo(() => {
    if (!diagramMermaid) return "";
    const b64 = encodeMermaidForInk(diagramMermaid);
    if (!b64) return "";
    return `https://mermaid.ink/svg/${b64}`;
  }, [diagramMermaid]);

  const handleViewDiagram = async () => {
    if (!selectedProjectId) return;
    setDiagramOpen(true);
    setDiagramZoom(1);
    setDiagramPan({ x: 0, y: 0 });
    setShowMermaidText(false);
    setDiagramLoading(true);
    setDiagramError("");
    setDiagramMermaid("");
    try {
      const qs = new URLSearchParams({ project_id: selectedProjectId });
      const res = await fetch(`/api/dev-ide/db/diagram?${qs.toString()}`);
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.success) {
        setDiagramMermaid(data.mermaid || "");
      } else {
        setDiagramError((data && data.error) || "Diagram failed.");
      }
    } catch (e) {
      setDiagramError(e.message || String(e));
    } finally {
      setDiagramLoading(false);
    }
  };

  const handleCopyMermaid = async () => {
    try {
      if (!diagramMermaid) return;
      await navigator.clipboard.writeText(diagramMermaid);
    } catch (e) {
      // ignore
    }
  };

  const tableOptions = useMemo(() => {
    const all = [...(tables || []), ...(views || [])];
    return all.sort((a, b) => String(a).localeCompare(String(b)));
  }, [tables, views]);

  return (
    <Box sx={{ p: 2, height: "100%", boxSizing: "border-box" }}>
      <Paper sx={{ p: 2, height: "100%", overflow: "auto" }}>
        <Stack spacing={2}>
          <Typography variant="h6">DB Explorer (Read-only MVP)</Typography>

          {projectsError ? <Alert severity="error">{projectsError}</Alert> : null}

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="db-project-label">Project</InputLabel>
              <Select
                labelId="db-project-label"
                label="Project"
                value={selectedProjectId || ""}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                disabled={projectsLoading || (projects || []).length === 0}
              >
                {(projects || []).map((p) => (
                  <MenuItem key={p.project_id} value={p.project_id}>
                    {p.label ? p.label : p.project_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadSchema}
              disabled={!selectedProjectId}
            >
              Download Full Schema
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleViewDiagram}
              disabled={!selectedProjectId}
            >
              View Diagram
            </Button>

            {projectsLoading ? <CircularProgress size={18} /> : null}
          </Stack>

          <Divider />

          {tablesError ? <Alert severity="error">{tablesError}</Alert> : null}

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="db-table-label">Table / View</InputLabel>
              <Select
                labelId="db-table-label"
                label="Table / View"
                value={selectedTable || ""}
                onChange={(e) => setSelectedTable(e.target.value)}
                disabled={tablesLoading || !selectedProjectId}
              >
                {tableOptions.length === 0 ? (
                  <MenuItem value="">
                    <em>No tables</em>
                  </MenuItem>
                ) : (
                  tableOptions.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              onClick={handleDescribe}
              disabled={!canAct || resultLoading}
            >
              Describe
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={() => handleSelectTop(50)}
              disabled={!canAct || resultLoading}
            >
              Select Top 50
            </Button>

            {tablesLoading || resultLoading ? <CircularProgress size={18} /> : null}
          </Stack>

          {resultError ? <Alert severity="error">{resultError}</Alert> : null}

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Output
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1,
                backgroundColor: "rgba(0,0,0,0.02)",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                minHeight: 240,
              }}
            >
              {describeResult ? JSON.stringify(describeResult, null, 2) : null}
              {!describeResult && selectResult ? JSON.stringify(selectResult, null, 2) : null}
              {!describeResult && !selectResult ? (
                <span style={{ color: "rgba(0,0,0,0.6)" }}>
                  Choose a project and table, then click Describe or Select Top 50.
                </span>
              ) : null}
            </Paper>
          </Box>

          <Alert severity="info">
            This MVP is read-only: it only uses SHOW / DESCRIBE / SELECT with a bounded LIMIT.
            It reads DB credentials from the selected project&apos;s .env file.
          </Alert>

          <Dialog open={diagramOpen} onClose={() => setDiagramOpen(false)} maxWidth="xl" fullWidth>
            <DialogTitle>DB Diagram (Mermaid ERD)</DialogTitle>
            <DialogContent dividers sx={{ height: "80vh", display: "flex", flexDirection: "column", gap: 1 }}>
              {diagramError ? <Alert severity="error" sx={{ mb: 2 }}>{diagramError}</Alert> : null}
              {diagramLoading ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Generating diagram…</Typography>
                </Stack>
              ) : null}

              {!diagramLoading && diagramSvgUrl ? (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 1,
                    overflow: "hidden",
                    backgroundColor: "rgba(0,0,0,0.01)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack direction="row" spacing={1} sx={{ p: 1, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDiagramZoom((z) => Math.min(5, +(z * 1.2).toFixed(3)))}
                    >
                      Zoom +
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDiagramZoom((z) => Math.max(0.2, +(z / 1.2).toFixed(3)))}
                    >
                      Zoom -
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setDiagramZoom(1);
                        setDiagramPan({ x: 0, y: 0 });
                      }}
                    >
                      Reset
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.8, alignSelf: "center" }}>
                      {Math.round(diagramZoom * 100)}%
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "auto",
                      cursor: diagramIsPanning ? "grabbing" : "grab",
                      userSelect: "none",
                    }}
                    onMouseDown={(e) => {
                      setDiagramIsPanning(true);
                      setDiagramPanStart({ x: e.clientX - diagramPan.x, y: e.clientY - diagramPan.y });
                    }}
                    onMouseMove={(e) => {
                      if (!diagramIsPanning) return;
                      setDiagramPan({ x: e.clientX - diagramPanStart.x, y: e.clientY - diagramPanStart.y });
                    }}
                    onMouseUp={() => setDiagramIsPanning(false)}
                    onMouseLeave={() => setDiagramIsPanning(false)}
                    onWheel={(e) => {
                      // Zoom with Ctrl+wheel (prevents accidental zoom while scrolling)
                      if (!e.ctrlKey) return;
                      e.preventDefault();
                      const delta = e.deltaY;
                      setDiagramZoom((z) => {
                        const next = delta > 0 ? z / 1.1 : z * 1.1;
                        return Math.max(0.2, Math.min(5, +next.toFixed(3)));
                      });
                    }}
                  >
                    <img
                      src={diagramSvgUrl}
                      alt="DB ERD"
                      style={{
                        transform: `translate(${diagramPan.x}px, ${diagramPan.y}px) scale(${diagramZoom})`,
                        transformOrigin: "0 0",
                        display: "block",
                        maxWidth: "none",
                      }}
                    />
                  </Box>
                </Box>
              ) : null}

              {!diagramLoading && !diagramSvgUrl && !diagramError ? (
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  No diagram to display.
                </Typography>
              ) : null}

              {diagramMermaid && showMermaidText ? (
                <Paper
                  variant="outlined"
                  sx={{ mt: 2, p: 1, backgroundColor: "rgba(0,0,0,0.02)", fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                >
                  {diagramMermaid}
                </Paper>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCopyMermaid} disabled={!diagramMermaid}>Copy Mermaid</Button>
              <Button onClick={() => setShowMermaidText((v) => !v)} disabled={!diagramMermaid}>
                {showMermaidText ? "Hide Mermaid" : "Show Mermaid"}
              </Button>
              <Button onClick={() => setDiagramOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Paper>
    </Box>
  );
}